import _extends from "@babel/runtime/helpers/extends";
import classnames from "classnames/dedupe";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { Checkbox, InlineAlert, Spinner } from "../index";
import { SelectableTableRow } from "./components/selectable-table-row";
/**
 * NestedSelectableTable component
 * @param {Object} props
 * @param {any[]} [props.headings]
 * @param {SelectableRow[]} [props.rows]
 * @param {string[]} [props.selectedRows] a collection of selected row ids
 * @param {any} props.className
 * @param {(selectedRows: string[]) => {}} [props.onChange]
 * @param {(checked: boolean) => {}} [props.onToggleSelectAll]
 * @param {boolean} [props.isLoading]
 * @param {boolean} [props.isError]
 * @param {boolean} [props.isSelectable] defaults to true
 * @param {any} [props.EmptyState]
 * @param {string} [props.searchText]
 * @param {(props: { index: number, [key: string]: any }) => JSX.Element} [props.TableRow]
 * @param {(props: { [key: string]: any }) => JSX.Element} [props.TableHeaderRow]
 * @returns
 */

export const NestedSelectableTable = ({
  headings = [],
  rows = [],
  selectedRows = [],
  className,
  onChange = selected => ({
    selected
  }),
  onToggleSelectAll = checked => ({
    checked
  }),
  isLoading = false,
  isError = false,
  isSelectable = true,
  EmptyState = "No data found",
  searchText = "",
  TableHeaderRow,
  TableRow,
  ...props
}) => {
  const classes = classnames(["table-auto w-full bg-white rounded"], className);
  const colspan = headings.length;
  const [openRows, setOpenRows] = useState([]);

  const toggleOpenRows = id => openRows.includes(id) ? setOpenRows(openRows.filter(r => r !== id)) : setOpenRows(openRows.concat(id));

  const toggleRowAndChildren = r => onChange((selectedRows.includes(r?.id) ? [r?.id].concat(r?.children?.map(c => c?.id).filter(c => selectedRows.includes(c))) : [r?.id].concat(r?.children?.map(c => c?.id).filter(c => !selectedRows.includes(c)))).filter(Boolean));

  const filterBySearchText = row => !searchText || searchText === "" ? true : row.columns.some(c => c?.toString()?.toLowerCase()?.includes(searchText?.toLowerCase()));

  const filteredRows = rows.filter(filterBySearchText);
  return /*#__PURE__*/React.createElement("table", _extends({
    className: classes
  }, props), headings ? /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement(TableHeaderRow, null, /*#__PURE__*/React.createElement(React.Fragment, null, isSelectable ? /*#__PURE__*/React.createElement("th", {
    className: "px-4 py-2"
  }, /*#__PURE__*/React.createElement(Checkbox, {
    name: "select-all",
    value: "all",
    checked: selectedRows.includes("all"),
    onChange: onToggleSelectAll
  })) : null, headings.map(Heading => /*#__PURE__*/React.createElement("th", {
    key: Heading,
    className: "px-4 py-2"
  }, typeof Heading === "function" ? /*#__PURE__*/React.createElement(Heading, null) : Heading))))) : null, /*#__PURE__*/React.createElement("tbody", {
    className: "bg-gray-100"
  }, isLoading ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    className: "px-4 py-2 text-center",
    colSpan: 4
  }, /*#__PURE__*/React.createElement(Spinner, {
    size: 15
  }))) : filteredRows.length ? filteredRows.map((r, rowIndex) => /*#__PURE__*/React.createElement(TableRow, {
    index: rowIndex,
    key: r.id,
    id: r.id,
    columns: r.columns,
    isOpen: openRows.includes(r.id),
    onToggle: toggleOpenRows,
    selected: selectedRows.includes(r.id) || selectedRows.includes("all"),
    isSelectable: isSelectable // @ts-ignore
    ,
    onChange: () => toggleRowAndChildren(r)
  }, r.children ? /*#__PURE__*/React.createElement(NestedSelectableTable, {
    rows: r.children,
    selectedRows: selectedRows,
    className: className,
    onChange: onChange,
    isSelectable: isSelectable
  }) : null)) : /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    className: "border px-4 py-2 text-center",
    colSpan: colspan
  }, isError ? /*#__PURE__*/React.createElement(InlineAlert, {
    className: "my-8 px-8 text-center",
    valid: false
  }, /*#__PURE__*/React.createElement("p", null, "Sorry, there was an error loading this data")) : searchText ? /*#__PURE__*/React.createElement("h4", {
    className: "text-xl font-bold my-8"
  }, "No item matches this filter") : typeof EmptyState === "function" ? /*#__PURE__*/React.createElement(EmptyState, null) : /*#__PURE__*/React.createElement("h4", {
    className: "text-xl font-bold my-8"
  }, EmptyState)))));
};
/**
 *
 * @param {{ className: string, children: any }} props
 * @returns {JSX.Element}
 */
// eslint-disable-next-line react/prop-types

const TableHeaderRow = ({
  className,
  children
}) => {
  const tableClassName = classnames("bg-en-gray-200 text-en-gray-900", className);
  return /*#__PURE__*/React.createElement("tr", {
    className: tableClassName
  }, children);
};

NestedSelectableTable.defaultProps = {
  TableRow: SelectableTableRow,
  TableHeaderRow
};
NestedSelectableTable.propTypes = {
  headings: PropTypes.arrayOf(PropTypes.string),
  rows: PropTypes.array,
  className: PropTypes.any,
  selectedRows: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  onToggleSelectAll: PropTypes.func,
  children: PropTypes.any,
  EmptyState: PropTypes.any,
  searchText: PropTypes.string,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  isSelectable: PropTypes.bool,
  TableRow: PropTypes.func,
  TableHeaderRow: PropTypes.func
};
/**
 *
 * @typedef {Object} SelectableRow
 * @property {string} id
 * @property {any[]} columns
 * @property {SelectableRow[]} [children]
 */

export * from "./components";